import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import SideBar from "../components/SideBar";
import TopBar from "../components/TopBar";
import members from "../assets/image/members.svg";
import announcement from "../assets/image/announcement.svg";
import events from "../assets/image/event.svg";
import logo from "../assets/image/rccglogo.webp";

window.Pusher = Pusher;

function Dashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalMembers, setTotalMembers] = useState(0);
  const [unreadAnnouncements, setUnreadAnnouncements] = useState(0);
  const [totalEvents, setTotalEvents] = useState(0);
  const [inAppNotificationsEnabled, setInAppNotificationsEnabled] =
    useState(true);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const eventTriggeredRef = React.useRef(false);
  const TEST_BASE_URL = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    console.log("=== COMPONENT MOUNTED ===");
    console.log("Initial localStorage value:", localStorage.getItem("unreadAnnouncements"));
  }, []);

  useEffect(() => {
    const fetchNotificationSettings = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) return;

        const response = await fetch(`${TEST_BASE_URL}/notification-settings`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const raw = data?.data || {};
          setInAppNotificationsEnabled(Boolean(raw.in_app));
        }
      } catch (error) {
        console.error("Error fetching notification settings:", error);
      }
    };

    fetchNotificationSettings();
  }, [TEST_BASE_URL]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          toast.error("Please log in to access your dashboard.", {
            theme: "colored",
          });
          return;
        }

        const headers = {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        };

        const res = await fetch(`${TEST_BASE_URL}/dashboard`, { headers });

        if (res.status === 401) {
          toast.error("Session expired. Please log in again.", {
            theme: "colored",
          });
          localStorage.removeItem("authToken");
          window.location.href = "/login";
          return;
        }

        const data = await res.json();
        
        console.log("=== DASHBOARD API RESPONSE ===");
        console.log("Full response:", data);
        console.log("data.data:", data.data);
        console.log("unread_announcement_count:", data.data?.unread_announcement_count);
        console.log("Type of unread_announcement_count:", typeof data.data?.unread_announcement_count);
        console.log("localStorage before update:", localStorage.getItem("unreadAnnouncements"));

        setTotalMembers(data.data?.total_user_count ?? 0);

        if (typeof data.data?.unread_announcement_count === "number") {
          console.log("✅ Setting unread announcements to:", data.data.unread_announcement_count);
          setUnreadAnnouncements(data.data.unread_announcement_count);
          localStorage.setItem(
            "unreadAnnouncements",
            data.data.unread_announcement_count.toString()
          );
          console.log("localStorage after update:", localStorage.getItem("unreadAnnouncements"));
        } else {
          console.log("❌ unread_announcement_count is NOT a number or is undefined");
        }

        setTotalEvents(data.data?.upcoming_event_count ?? 0);
      } catch (err) {
        console.error("Dashboard error:", err);
        toast.error("Network error — please try again.", { theme: "colored" });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [TEST_BASE_URL]);

  useEffect(() => {
    const echo = new Echo({
      broadcaster: "reverb",
      key: "6z4phgto7eyxfwkptuq9",
      wsHost: "rccgbournemouth.org",
      wsPort: 443,
      wssPort: 443,
      forceTLS: true,
      enabledTransports: ["ws", "wss"],
    });

    echo.channel("dashboard").listen(".members.updated", (event) => {
      setTotalMembers(event.total_members);
    });

    echo.channel("notifications").listen(".incoming.notifications", (event) => {
      console.log("=== WEBSOCKET NOTIFICATION EVENT ===");
      console.log("Full event:", event);
      console.log("announcement_count:", event.announcement_count);
      console.log("Type of announcement_count:", typeof event.announcement_count);

      if (typeof event.announcement_count === "number") {
        console.log("✅ WebSocket updating unread announcements to:", event.announcement_count);
        setUnreadAnnouncements(event.announcement_count);
        localStorage.setItem("unreadAnnouncements", event.announcement_count.toString());
        console.log("localStorage after WebSocket update:", localStorage.getItem("unreadAnnouncements"));
      } else {
        console.log("❌ announcement_count from WebSocket is NOT a number");
      }
    });

    echo.channel("dashboard").listen(".dashboard.updated", (event) => {
      eventTriggeredRef.current = true;
      setTotalEvents(event.total_event_count);
    });

    return () => echo.disconnect();
  }, []);

  useEffect(() => {
    if (
      eventTriggeredRef.current &&
      inAppNotificationsEnabled &&
      totalEvents > 0
    ) {
      toast.success(
        `New event has been created, check notifications for details`,
        { theme: "colored" }
      );
      eventTriggeredRef.current = false;
    }
  }, [totalEvents, inAppNotificationsEnabled]);

  useEffect(() => {
    const inAppEnabled = localStorage.getItem("inAppNotifications") === "true";
    setInAppNotificationsEnabled(inAppEnabled);
  }, []);

  return (
    <>
      <section className="dashboard">
        <SideBar isOpen={sidebarOpen} />
        <div className="body-content">
          <TopBar onToggleSidebar={toggleSidebar} />
          <div className="bottom-content">
            <h5>Welcome to RCCG, New Beginning Chapel Membership Portal</h5>
            {loading ? (
              <p className="text-center my-5">Loading dashboard...</p>
            ) : (
              <>
                <div className="row dashboardrow">
                  <div className="col-md-4">
                    <div className="dashboard-card">
                      <div>
                        <h6>Total Members Today:</h6>
                        <h2 className="members">{totalMembers}</h2>
                        <p>
                          Total verified members in the church community on this
                          portal
                        </p>
                      </div>
                      <img src={members} alt="" width="90px" />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="dashboard-card">
                      <div>
                        <h6>Unread Announcements</h6>
                        <h2 className="announce">{unreadAnnouncements}</h2>
                        <p>
                          Track unread announcements to stay updated and
                          informed.
                        </p>
                      </div>
                      <img src={announcement} alt="" width="90px" />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="dashboard-card">
                      <div>
                        <h6>Upcoming Events</h6>
                        <h2 className="event">{totalEvents}</h2>
                        <p>
                          Quick insights into all upcoming events and
                          attendance.
                        </p>
                      </div>
                      <img src={events} alt="" width="90px" />
                    </div>
                  </div>
                </div>

                <div className="explore-card">
                  <h5>Explore</h5>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="blue-card">
                        <img src={logo} alt="" />
                        <h6>Resources</h6>
                        <p className="text-white">
                          Tools and wisdom for building a strong, faith-filled
                          life
                        </p>
                        <a href="/resources">Learn More</a>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="blue-card">
                        <img src={logo} alt="" />
                        <h6>Singles' Fellowship & Growth</h6>
                        <p className="text-white">
                          A space for single adults to connect, serve, and
                          thrive in Christ.
                        </p>
                        <a href="/singlesfellowship">Learn More</a>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="blue-card">
                        <img src={logo} alt="" />
                        <h6>Volunteering</h6>
                        <p className="text-white">
                          Serve with purpose in your church community.
                        </p>
                        <a href="/volunteering">Learn More</a>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

export default Dashboard;